import { useEffect, useRef, useState } from "react";
import { useInstantSearch } from "react-instantsearch";
import { useCsrfToken } from "@/hooks/use-csrf-token";
import { boundingBoxFilter, postMsearch, refinementTermsFilters } from "@/lib/msearch-filters";
import {
  BUDGET_HISTOGRAM_BAR_COUNT,
  BUDGET_HISTOGRAM_INTERVAL_CENTS,
  BUDGET_MAX_CENTS,
  BUDGET_MIN_CENTS,
} from "./filter-values";

// Matches `useDestinationsSearch` / `DESTINATION_PINS` — one front door, one index.
const HOST = "/api/destinations";
const INDEX = "destinations";
const GEO_FIELD = "location";

interface HistogramBucket {
  key: number;
  doc_count: number;
}

interface HistogramResponse {
  aggregations?: { budget?: { buckets?: HistogramBucket[] } };
}

// Costs cheaper than the slider floor pile into the first bar and dearer than
// its ceiling into the last, so the tails stay visible instead of vanishing
// off-scale.
function foldBuckets(buckets: HistogramBucket[]): number[] {
  const distribution: number[] = new Array(BUDGET_HISTOGRAM_BAR_COUNT).fill(0);
  for (const bucket of buckets) {
    const raw = Math.floor((bucket.key - BUDGET_MIN_CENTS) / BUDGET_HISTOGRAM_INTERVAL_CENTS);
    const index = Math.min(Math.max(raw, 0), BUDGET_HISTOGRAM_BAR_COUNT - 1);
    distribution[index] = (distribution[index] ?? 0) + bucket.doc_count;
  }
  return distribution;
}

// The dialog unmounts its body on close, so instance state alone would flash
// an empty chart on every open. The last fold survives here, keyed by the
// filters that produced it, and seeds the next mount while a fresh fetch runs.
let lastResult: { key: string; distribution: number[] } | null = null;

/**
 * Live price distribution behind the budget slider: a `histogram` aggregation
 * on `cost_total_cents` over the results the OTHER filters produce. The budget
 * itself lives in InstantSearch `range` uiState, which this hook never reads —
 * the bars describe what the range selection selects from, so they must not
 * shrink to the selection (the Airbnb behaviour).
 */
export function useBudgetHistogram(): { distribution: number[] | undefined } {
  const csrfToken = useCsrfToken();
  const { uiState } = useInstantSearch();
  const indexUiState = uiState?.[INDEX];
  const refinementList = indexUiState?.refinementList as Record<string, string[]> | undefined;
  const boundsBox = indexUiState?.configure?.insideBoundingBox;

  // Serialise so the effect refetches on a real value change, not the identity
  // churn `uiState` produces every render.
  const refinementKey = JSON.stringify(refinementList ?? null);
  const boundsKey = typeof boundsBox === "string" ? boundsBox : null;
  const resultKey = `${refinementKey}|${boundsKey ?? ""}`;

  const [distribution, setDistribution] = useState<number[] | undefined>(() =>
    lastResult?.key === resultKey ? lastResult.distribution : undefined,
  );
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!csrfToken) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const filter = refinementTermsFilters(JSON.parse(refinementKey) as Record<string, string[]> | null);
    if (boundsKey) {
      const box = boundingBoxFilter(boundsKey, GEO_FIELD);
      if (box) filter.push(box);
    }

    const body = {
      size: 0,
      query: filter.length ? { bool: { filter } } : { match_all: {} },
      aggs: {
        budget: {
          histogram: {
            field: "cost_total_cents",
            interval: BUDGET_HISTOGRAM_INTERVAL_CENTS,
            // Buckets align to multiples of the interval from zero by default;
            // the offset re-anchors them on the slider floor so every bucket
            // maps 1:1 onto a bar.
            offset: BUDGET_MIN_CENTS % BUDGET_HISTOGRAM_INTERVAL_CENTS,
            // Guarantees every in-range bucket exists (zeros included) so the
            // fold always yields the full bar count.
            extended_bounds: { min: BUDGET_MIN_CENTS, max: BUDGET_MAX_CENTS - BUDGET_HISTOGRAM_INTERVAL_CENTS },
          },
        },
      },
    };

    postMsearch<HistogramResponse>(HOST, INDEX, body, { csrfToken, signal: controller.signal })
      .then((result) => {
        const folded = foldBuckets(result.aggregations?.budget?.buckets ?? []);
        lastResult = { key: resultKey, distribution: folded };
        setDistribution(folded);
      })
      .catch((error) => {
        if ((error as Error).name === "AbortError") return;
        // Keep the last-known bars — the chart is decorative — but a real
        // failure must stay visible somewhere or a dead histogram ships silently.
        console.error("budget histogram request failed", error);
      });

    return () => controller.abort();
  }, [csrfToken, refinementKey, boundsKey, resultKey]);

  return { distribution };
}
